import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  CreateSavedQueryDTO,
  SavedQueryDTO,
  SavedQueryHistoryDTO,
} from "../../../types/SavedQuery";
import { getBaseQuery } from "../baseQuery";

export const savedQueryService = createApi({
  reducerPath: "savedQueryService",
  baseQuery: getBaseQuery("orpheus"),
  tagTypes: ["SavedQueries"],
  endpoints: (builder) => ({
    getSavedQueries: builder.query<SavedQueryDTO[], void>({
      query: () => {
        return "/api/saved-queries";
      },
      providesTags: [{ type: "SavedQueries", id: "LIST" }],
    }),
    getSavedQueryHistory: builder.query<SavedQueryHistoryDTO[], { savedQueryId: string }>(
      {
        query: ({ savedQueryId }) => `/api/saved-queries/${savedQueryId}/history`,
        providesTags: [{ type: "SavedQueries", id: "HISTORY" }],
      },
    ),
    getSavedQueryById: builder.query<SavedQueryDTO, { savedQueryId: string }>({
      query: (arg) => {
        const { savedQueryId } = arg;
        return `/api/saved-queries/${savedQueryId}`;
      },
      providesTags: [{ type: "SavedQueries", id: "DETAIL" }],
    }),
    createSavedQuery: builder.mutation<
      SavedQueryDTO,
      { savedQuery: CreateSavedQueryDTO }
    >({
      query: (arg) => {
        const savedQuery = { ...arg.savedQuery, id: undefined };
        return {
          url: `/api/saved-queries`,
          method: "POST",
          body: savedQuery,
        };
      },
      invalidatesTags: [{ type: "SavedQueries", id: "LIST" }],
    }),
    deleteSavedQuery: builder.mutation<void, { savedQueryId: string }>({
      query: (arg) => ({
        url: `/api/saved-queries/${arg.savedQueryId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "SavedQueries", id: "LIST" }],
    }),
    updateSavedQueryById: builder.mutation<
      SavedQueryDTO,
      { savedQuery: CreateSavedQueryDTO }
    >({
      query: (arg) => {
        const { savedQuery } = arg;
        return {
          url: `/api/saved-queries/${savedQuery.id}`,
          method: "PUT",
          body: savedQuery,
        };
      },
      invalidatesTags: [{ type: "SavedQueries", id: "LIST" }],
    }),
  }),
});

export const {
  useGetSavedQueriesQuery,
  useGetSavedQueryHistoryQuery,
  useGetSavedQueryByIdQuery,
  useCreateSavedQueryMutation,
  useDeleteSavedQueryMutation,
  useUpdateSavedQueryByIdMutation,
} = savedQueryService;
